// src/lib/cropImage.ts

/**
 * Loads an image and returns an HTMLImageElement that is fully decoded.
 */
export function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    // No `img.crossOrigin = 'anonymous'`. Inputs are always data: URLs
    // (canvas.toDataURL, FileReader, resizeImage) — CORS is not in the
    // picture, and on some Android WebViews setting crossOrigin before
    // the scheme is resolved can cause the load to fail.
    img.onload = () => {
      if (img.complete && img.naturalWidth > 0) {
        resolve(img);
      } else {
        reject(new Error('Image failed to load'));
      }
    };
    img.onerror = () => reject(new Error('Failed to load image'));
    img.src = url;
  });
}

/**
 * Crops the image to the given pixel area, resizes to max 1280px on the long
 * edge. Returns a blob URL. The caller is responsible for revoking it via
 * `URL.revokeObjectURL` after it has consumed the URL — the ImageCropper
 * does this right after `blobUrlToDataUrl` finishes, so the leak per crop
 * is zero.
 *
 * v0.4.9.3.4c8 — Removed forced minimum upscaling.
 *
 *   Previously, if a crop was smaller than 960px on the long edge, it was
 *   upscaled to 960px. This destroyed OCR accuracy on tight crops.
 *   Stretching a sharp 200x50 pixel crop to 960x240 creates blurry,
 *   interpolated pixels. The OCR model would then fail to read the
 *   characters, often only recognizing the easiest part (e.g., the "BY"
 *   country code).
 *
 *   Fix: We now only DOWNSCALE if the crop is larger than MAX_DIM (1280px).
 *   We never UPSCALE. A small, sharp crop is infinitely better for OCR
 *   than a large, blurry one.
 *
 * v0.4.9.3.4c7 — DOM→natural scaling fix:
 *
 *   The c6 code computed `factorX = image.naturalWidth / image.width` from
 *   the detached image loaded INSIDE this function. That detached image
 *   has no DOM layout, so its `width`/`height` HTML attributes default
 *   to its intrinsic dimensions — which are the SAME as `naturalWidth`
 *   and `naturalHeight`. So `factorX` was always 1, no matter what.
 *
 *   The calling component passes the actual RENDERED DOM dimensions of
 *   the img (via `imgRef.current.clientWidth` and `clientHeight`) to
 *   calculate the correct scaling factor.
 */
export async function getCroppedImg(
  imageSrc: string,
  pixelCrop: { x: number; y: number; width: number; height: number },
  renderedWidth?: number,
  renderedHeight?: number
): Promise<string> {
  // v0.4.9.4.6c5 — P0 B2B OUTAGE FIX. renderedWidth/renderedHeight are now
  // OPTIONAL.
  //
  // v0.4.9.3.4c7 made the DOM→natural conversion mandatory by adding two
  // REQUIRED parameters. That was correct for the one caller that needed
  // them and broke every caller that did not:
  //
  //   B2G  ImageCropper.tsx     react-image-crop. pixelCrop is in
  //                             DOM/RENDERED space, so it passes
  //                             clientWidth/clientHeight explicitly and the
  //                             conversion is required. UNCHANGED.
  //
  //   B2B  AuthorizedManagers   react-easy-crop. croppedAreaPixels is
  //       .tsx (+ POIFormModal,  ALREADY in natural image space, so the
  //       POIGalleryEditor)     correct behaviour is identity (factor = 1).
  //                             These callers pass two arguments. They
  //                             have always relied on the pre-c7
  //                             two-argument signature, and c7 made them
  //                             throw before reaching the crop at all —
  //                             "cannot save Authorized Persons", in a live
  //                             pilot.
  //
  // Contract, enforced below:
  //   both undefined        -> identity            (B2B path)
  //   both positive finite  -> DOM->natural        (B2G path)
  //   anything else         -> throw, caller bug
  //
  // A half-specified pair (one passed, one not) is a caller bug, not an
  // implicit identity — silently ignoring it would reintroduce exactly the
  // c6/c7 class of silent mis-crop.
  const rw = renderedWidth;
  const rh = renderedHeight;
  const bothUndefined = rw === undefined && rh === undefined;
  const bothValid =
    typeof rw === 'number' && Number.isFinite(rw) && rw > 0 &&
    typeof rh === 'number' && Number.isFinite(rh) && rh > 0;

  if (!bothUndefined && !bothValid) {
    throw new Error(
      `getCroppedImg: pass BOTH renderedWidth and renderedHeight as positive ` +
      `numbers, or pass NEITHER. Got ${rw}x${rh}.`
    );
  }

  const image = await loadImage(imageSrc);

  const ctx = document.createElement('canvas').getContext('2d') as CanvasRenderingContext2D | null;
  if (!ctx) throw new Error('No 2d context');

  // DOM -> natural conversion, only when the caller supplied rendered dims.
  // Undefined dims => the caller's crop is already in natural space.
  const factorX = bothValid ? image.naturalWidth / (rw as number) : 1;
  const factorY = bothValid ? image.naturalHeight / (rh as number) : 1;

  const x = pixelCrop.x * factorX;
  const y = pixelCrop.y * factorY;
  const width = pixelCrop.width * factorX;
  const height = pixelCrop.height * factorY;

  // Clamp the natural crop area to the source image bounds — a user who
  // panned to the very edge can produce a slightly oversized crop.
  const safeX = Math.max(0, Math.min(x, image.naturalWidth - 1));
  const safeY = Math.max(0, Math.min(y, image.naturalHeight - 1));
  const safeWidth = Math.min(width, image.naturalWidth - safeX);
  const safeHeight = Math.min(height, image.naturalHeight - safeY);

  // Output size: clamp the long edge to 1280px. Do NOT force a minimum
  // upscale, as it destroys OCR accuracy on tight crops.
  const MAX_DIM = 1280;
  let outWidth = safeWidth;
  let outHeight = safeHeight;
  
  if (Math.max(safeWidth, safeHeight) > MAX_DIM) {
    const downscale = MAX_DIM / Math.max(safeWidth, safeHeight);
    outWidth = Math.round(safeWidth * downscale);
    outHeight = Math.round(safeHeight * downscale);
  }

  // Allocate the output canvas and draw the natural crop in a single
  // drawImage call. Crop coords are in natural pixel space.
  ctx.canvas.width = outWidth;
  ctx.canvas.height = outHeight;
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(
    image,
    safeX, safeY, safeWidth, safeHeight, // source crop from the decoded image
    0, 0, outWidth, outHeight             // destination on the output canvas
  );

  return new Promise((resolve, reject) => {
    ctx.canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error('Canvas is empty'));
          return;
        }
        resolve(URL.createObjectURL(blob));
      },
      'image/jpeg',
      0.95
    );
  });
}